import { Button, Form, Modal, Space, Typography, notification } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';
import DebounceSelect from 'components/debounce-select/select';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';

/** Một lựa chọn trong ô tìm kiếm (labelInValue của antd Select). */
export type PickOption = { value: number; label: string };

/** Nhãn người dùng trong ô chọn: "Tên hiển thị (username) — email". */
export function buildUserLabel(user: any): string {
  const full = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  const head = user?.nickName || full || user?.username || user?.email || `User #${user?.id}`;
  const username = user?.username && user.username !== head ? ` (${user.username})` : '';
  const identity = user?.email ? `${head}${username} — ${user.email}` : `${head}${username}`;
  return user?.familyId ? `${identity} · Nhóm #${user.familyId}` : identity;
}

/**
 * Tìm người dùng cho ô chọn (GET /users/search/admin — khớp username, email, UID TikTok;
 * bỏ tài khoản đã khoá). Cần ít nhất 2 ký tự.
 */
export const searchUserOptions = async (search: string): Promise<PickOption[]> => {
  const query = search.trim();
  if (query.length < 2) return [];
  try {
    const res: any = await AxiosUtils.get(API.searchUserAdmin, { query, limit: 20 });
    const list: any[] = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
    return list.map((user) => ({ value: user.id, label: buildUserLabel(user) }));
  } catch {
    return [];
  }
};

export interface AddMembersFormProps {
  /** Đối tượng đang thao tác (nhóm / sự kiện). Có thì cố định, không cho đổi. */
  target?: PickOption;
  /** Chưa có `target`: cho tìm và chọn đối tượng bằng hàm này. */
  fetchTargets?: (search: string) => Promise<PickOption[]>;
  /** Tên loại đối tượng, vd. "Nhóm quản lý", "Sự kiện bình chọn". */
  targetLabel: string;
  targetPlaceholder?: string;
  /** Tên người được thêm, vd. "Thành viên", "Người chơi". */
  userLabel?: string;
  userHint?: string;
  /** Id người đã có trong đối tượng: vẫn hiện trong kết quả nhưng không chọn được. */
  existingUserIds?: Array<number | string>;
  submitText?: string;
  /** Gọi API thêm. Reject để báo thất bại (form giữ nguyên lựa chọn để thử lại). */
  onSubmit: (userIds: number[], targetId: number) => Promise<unknown>;
  /** Sau khi thêm thành công — vd. tải lại danh sách, đóng modal. */
  onSuccess?: (userIds: number[], targetId: number) => void;
  /** Có thì hiện nút "Hủy". */
  onCancel?: () => void;
}

interface FormValues {
  target?: PickOption;
  users?: PickOption[];
}

/** Form thêm nhiều người vào một nhóm / sự kiện — dùng trong modal hoặc đặt thẳng trên trang. */
export const AddMembersForm = ({
  target,
  fetchTargets,
  targetLabel,
  targetPlaceholder,
  userLabel = 'Thành viên',
  userHint,
  existingUserIds,
  submitText = 'Thêm',
  onSubmit,
  onSuccess,
  onCancel,
}: AddMembersFormProps) => {
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const userNoun = userLabel.toLowerCase();

  const existing = useMemo(() => new Set((existingUserIds || []).map(String)), [existingUserIds]);

  const fetchUsers = useCallback(
    async (search: string) => {
      const options = await searchUserOptions(search);
      if (!existing.size) return options;
      return options.map((option) =>
        existing.has(String(option.value))
          ? { ...option, label: `${option.label} (đã có)`, disabled: true }
          : option
      );
    },
    [existing]
  );

  const fetchTargetOptions = useCallback(
    async (search: string) => {
      if (!fetchTargets || !search.trim()) return [];
      try {
        return await fetchTargets(search.trim());
      } catch {
        return [];
      }
    },
    [fetchTargets]
  );

  const handleFinish = async (values: FormValues) => {
    const targetId = Number(target ? target.value : values.target?.value);
    const userIds = (values.users || []).map((item) => Number(item.value));
    if (!targetId || !userIds.length) return;
    setSubmitting(true);
    try {
      await onSubmit(userIds, targetId);
      notification.success({
        message: 'Thành công',
        description: `Đã thêm ${userIds.length} ${userNoun}.`,
        duration: 4,
      });
      form.resetFields(['users']);
      onSuccess?.(userIds, targetId);
    } catch (error: any) {
      // 403 đã được interceptor báo "Không đủ quyền".
      if (error?.statusCode !== 403) {
        notification.error({
          message: 'Không thành công',
          description: 'Có lỗi xảy ra, vui lòng thử lại.',
          duration: 4,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} autoComplete="off">
      {target ? (
        <Form.Item label={targetLabel}>
          <Typography.Text strong>{target.label}</Typography.Text>
        </Form.Item>
      ) : (
        <Form.Item
          name="target"
          label={targetLabel}
          rules={[{ required: true, message: `Chọn ${targetLabel.toLowerCase()}` }]}
        >
          <DebounceSelect
            size="middle"
            placeholder={targetPlaceholder || `Gõ tên ${targetLabel.toLowerCase()}`}
            fetchOptions={fetchTargetOptions}
            style={{ width: '100%' }}
          />
        </Form.Item>
      )}

      <Form.Item
        name="users"
        label={userLabel}
        extra={userHint || 'Gõ ít nhất 2 ký tự: username, email hoặc UID TikTok. Chọn được nhiều người.'}
        rules={[{ required: true, message: `Chọn ít nhất một ${userNoun}` }]}
      >
        <DebounceSelect
          size="middle"
          mode="multiple"
          placeholder={`Tìm ${userNoun}`}
          fetchOptions={fetchUsers}
          style={{ width: '100%' }}
        />
      </Form.Item>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Space>
          {onCancel && <Button onClick={onCancel}>Hủy</Button>}
          <Button type="primary" htmlType="submit" loading={submitting}>
            {submitText}
          </Button>
        </Space>
      </div>
    </Form>
  );
};

export interface AddMembersModalProps extends AddMembersFormProps {
  open: boolean;
  title: React.ReactNode;
  onCancel: () => void;
}

/**
 * Modal thêm thành viên/người chơi, mở ngay từ danh sách hoặc trang chi tiết
 * (không phải rời trang và chọn lại nhóm/sự kiện như trước).
 *
 * @example
 * <AddMembersModal
 *   open={!!clan}
 *   title="Thêm thành viên nhóm"
 *   targetLabel="Nhóm quản lý"
 *   target={{ value: clan.id, label: clan.title }}
 *   onSubmit={(userIds, groupId) => addMembersToClan(groupId, userIds)}
 *   onSuccess={() => { setClan(null); reload(); }}
 *   onCancel={() => setClan(null)}
 * />
 */
const AddMembersModal = ({ open, title, onCancel, target, ...formProps }: AddMembersModalProps) => {
  // Nơi gọi thường xoá `target` ngay khi đóng; giữ bản cuối để form không nháy sang
  // ô chọn đối tượng trong lúc modal còn đang chạy hiệu ứng đóng.
  const lastTarget = useRef(target);
  if (target) lastTarget.current = target;

  return (
    <Modal open={open} title={title} onCancel={onCancel} footer={null} destroyOnHidden>
      <AddMembersForm {...formProps} target={target ?? lastTarget.current} onCancel={onCancel} />
    </Modal>
  );
};

export default AddMembersModal;
